import { FrameType, HOST_EVENT_CHANNEL, encodeFrame, type FrameTransport, type HostEvent } from '@casa/bridge';

// A browser's bridge: one binary WebSocket to /bridge on the companion server that served the page
// (decision §11). The connection can drop (the server restarts, the Wi-Fi goes, a phone suspends
// the tab): the client is told (`onReset`) so whatever was open fails, and the transport connects
// again with back-off. A browser the server no longer knows goes to the pairing page.
//
// The browser is its own host for lifecycle: the page's visibility becomes the foreground and
// background events the shells send.

/** The server closes with this when the browser is not paired (any more). */
export const NOT_PAIRED_CLOSE_CODE = 4401;
export const RECONNECT_MIN_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;
/** Frames waiting for the connection; more means the server is gone and the app keeps asking. */
const MAX_QUEUED_FRAMES = 1_000;

interface SocketLike {
    binaryType: string;
    readonly readyState: number;
    send(data: ArrayBuffer | Uint8Array) : void;
    close() : void;
    onopen: (() => void) | null;
    onmessage: ((event: { data: unknown }) => void) | null;
    onclose: ((event: { code: number }) => void) | null;
}

/** What the transport uses of the page; a parameter so that tests can stand in for it. */
export interface BrowserPage {
    WebSocket: new (url: string) => SocketLike;
    location: { protocol: string; host: string; assign(url: string) : void };
    document?: { visibilityState: string; addEventListener(type: 'visibilitychange', listener: () => void) : void };
    fetch(url: string) : Promise<{ ok: boolean; json() : Promise<unknown> }>;
    setTimeout(callback: () => void, ms: number) : unknown;
    clearTimeout(timer: unknown) : void;
}

const OPEN = 1;

/** The companion server's bridge for a page it served; null for a page from anywhere else. */
export function serverTransport(page: BrowserPage) : FrameTransport | null {
    const { location } = page;
    if (location.protocol !== 'https:' && location.protocol !== 'http:') {
        return null;
    }

    const url = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/bridge`;
    const frameListeners = new Set<(frame: Uint8Array) => void>();
    const resetListeners = new Set<(reason: string) => void>();
    let socket: SocketLike | undefined;
    let queue: Uint8Array[] = [];
    let delay = RECONNECT_MIN_MS;
    let timer: unknown;
    let leaving = false;

    const emit = (frame: Uint8Array) : void => {
        for (const listener of [...frameListeners]) {
            listener(frame);
        }
    };

    function goToPairing() : void {
        leaving = true;
        page.location.assign('/pair');
    }

    /**
     * Whether the server has a bridge at all; undefined when it cannot be asked or does not say.
     * tilez-cloud has none (casa-server CS-25): its pages (the cloud page, the account pages) are for
     * browsers that need not be paired, and use no bridge.
     */
    async function hasBridge() : Promise<boolean | undefined> {
        try {
            const response = await page.fetch('/api/server');
            const offers = ((await response.json()) as { offers?: unknown }).offers;

            return Array.isArray(offers) ? offers.includes('bridge') : undefined;
        } catch {
            return undefined;
        }
    }

    /** Whether the server still knows this browser; undefined when it cannot be asked. */
    async function isPaired() : Promise<boolean | undefined> {
        try {
            const response = await page.fetch('/api/session');
            const session = (await response.json()) as { paired?: unknown };

            return typeof session.paired === 'boolean' ? session.paired : undefined;
        } catch {
            return undefined;
        }
    }

    function reconnectLater() : void {
        timer = page.setTimeout(() => {
            timer = undefined;
            connect();
        }, delay);
        delay = Math.min(delay * 2, RECONNECT_MAX_MS);
    }

    function connect() : void {
        if (leaving || socket) {
            return;
        }

        const current = new page.WebSocket(url);
        current.binaryType = 'arraybuffer';
        socket = current;
        current.onopen = () => {
            delay = RECONNECT_MIN_MS;
            for (const frame of queue.splice(0)) {
                current.send(frame);
            }
        };
        current.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                emit(new Uint8Array(event.data));
            }
        };
        current.onclose = (event) => {
            socket = undefined;
            // Frames for the lost connection: the reset fails what they were for.
            queue = [];
            const notPaired = event.code === NOT_PAIRED_CLOSE_CODE;
            for (const listener of [...resetListeners]) {
                listener(notPaired ? 'this browser is not paired' : `connection closed (${event.code})`);
            }
            if (notPaired) {
                return goToPairing();
            }

            // A refused WebSocket shows the page no status: a server without a bridge is left alone;
            // otherwise the session says whether it was the pairing.
            void (async () => {
                if ((await hasBridge()) === false) {
                    leaving = true;

                    return;
                }

                if ((await isPaired()) === false) {
                    goToPairing();
                } else {
                    reconnectLater();
                }
            })();
        };
    }

    // Back in front: try at once instead of waiting out the back-off.
    page.document?.addEventListener('visibilitychange', () => {
        const state: HostEvent['state'] = page.document?.visibilityState === 'visible' ? 'foreground' : 'background';
        emit(encodeFrame({ type: FrameType.Event, channelId: HOST_EVENT_CHANNEL, payload: new TextEncoder().encode(JSON.stringify({ type: 'lifecycle', state })) }));
        if (state === 'foreground' && !socket && timer !== undefined) {
            page.clearTimeout(timer);
            timer = undefined;
            delay = RECONNECT_MIN_MS;
            connect();
        }
    });

    connect();

    return {
        post(frame) {
            if (socket?.readyState === OPEN) {
                return socket.send(frame);
            }
            if (queue.length >= MAX_QUEUED_FRAMES) {
                throw new Error('bridge: not connected to the server');
            }

            queue.push(frame);
            // While a reconnect waits out its back-off, the frame waits with it.
            if (timer === undefined) {
                connect();
            }
        },
        onFrame(listener) {
            frameListeners.add(listener);

            return () => frameListeners.delete(listener);
        },
        onReset(listener) {
            resetListeners.add(listener);

            return () => resetListeners.delete(listener);
        },
    };
}
